/**
 * Ce que l'annuaire dit d'une fiche : se réserve-t-elle en ligne, et
 * confirme-t-elle tout de suite (FIXERLIGHT-050) ?
 *
 * Pur, sans React ni I/O, donc couvrable exhaustivement — et surtout lisible
 * depuis les trois surfaces qui posent la question : la carte du listing, le
 * hero de la fiche, et le tri de la page ville. Une définition recopiée dans
 * chacune divergerait à la première évolution, et l'annuaire promettrait alors
 * autre chose que ce que le tunnel accepte.
 *
 * TOUT est défensif, et c'est le point central du module : l'aperçu Vercel
 * d'une PR front parle à l'API de STAGING, qui prend du retard sur master.
 * Tant que la PR api n'y est pas, `fixer_lite` est absent de chaque fiche —
 * pas `null`, absent. Ces fonctions doivent alors rendre l'état éteint sans
 * lever, sinon la page ville casse en aperçu et la fiche, qui est servie en
 * SSR, rend un 503 plutôt qu'un bloc vide.
 */

/** Le bloc d'état d'une fiche, ou un objet vide — jamais null, jamais throw. */
function state(shop) {
  const fixerLite = shop && shop.fixer_lite;
  return fixerLite && typeof fixerLite === 'object' ? fixerLite : {};
}

/** La fiche se réserve-t-elle par le tunnel Fixer Lite ? */
export function isFixerLiteEnabled(shop) {
  return state(shop).enabled === true;
}

/**
 * La fiche confirme-t-elle sans attendre l'accord du réparateur ?
 *
 * L'invariant « instantané implique réservable en ligne » est ré-appliqué ici
 * plutôt que supposé : l'API le garantit, mais une réponse plus ancienne
 * servie par un cache ou par staging pourrait porter l'un sans l'autre, et
 * l'annuaire promettrait une confirmation immédiate derrière un parcours
 * injoignable.
 */
export function isInstantBooking(shop) {
  const s = state(shop);
  return s.enabled === true && s.instant_booking === true;
}

/**
 * La fiche se réserve-t-elle en ligne, quel que soit le parcours ?
 *
 * `bookable` est le tunnel RJ classique, et il compte au même titre qu'un
 * compte Fixer Lite (décision de Fred du 21/09) : du point de vue du client,
 * les deux veulent dire « je prends rendez-vous depuis cette page ».
 */
export function isOnlineBookable(shop) {
  return isFixerLiteEnabled(shop) || !!(shop && shop.bookable);
}

/**
 * Le rang d'ordre du listing : 0 instantané, 1 réservable en ligne, 2 le reste.
 *
 * L'API rend déjà son listing dans cet ordre, mais n'expose PAS le rang : le
 * front le recalcule, parce qu'il doit de toute façon porter le chemin de
 * repli pour l'API staging en retard. Lire un champ quand il est là et
 * recalculer sinon ferait deux chemins de code pour un résultat identique, et
 * donc une divergence possible entre les deux.
 *
 * Contrepartie assumée : si l'API change sa définition de groupe, ce fichier
 * change dans le même mouvement.
 */
export function bookingGroup(shop) {
  if (isInstantBooking(shop)) return 0;
  if (isOnlineBookable(shop)) return 1;
  return 2;
}

/** Une durée exploitable en minutes, ou null. */
function toDuration(value) {
  const minutes = parseInt(value, 10);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
}

/**
 * Un prix exploitable en euros, ou null.
 *
 * L'API peut rendre une chaîne numérique : `is_numeric` côté PHP accepte
 * `"59.00"`, et le blob de réglages est plus vieux que son schéma.
 */
function toPrice(value) {
  if (value === null || value === undefined || value === '') return null;
  const price = Number(value);
  // Zéro est écarté comme une absence, jamais rendu « 0 € » : une prestation
  // gratuite est une promesse commerciale que personne n'a arbitrée, et le
  // blob de réglages accepte `0` sans le vouloir (`is_numeric(0)` est vrai).
  return Number.isFinite(price) && price > 0 ? price : null;
}

/**
 * Les prestations affichables de la fiche : libellé, durée, prix.
 *
 * Rien n'est rendu tant que `enabled` est faux, et c'est le même invariant
 * que pour l'instantané : une réponse plus ancienne servie par un cache ou par
 * staging pourrait porter des `services` sans le compte qui les rend
 * réservables, et la fiche afficherait des tarifs derrière un tunnel
 * injoignable.
 *
 * Défensive pour une autre raison encore : la fiche annuaire est rendue en SSR, et
 * `renderToString` qui lève ne produit pas une case vide mais un 503 servi au
 * crawler. Une entrée sans libellé exploitable est donc écartée plutôt que
 * rendue — c'est aussi ce que fait l'API, qui écarte celles sans clé.
 */
export function fixerLiteServices(shop) {
  if (!isFixerLiteEnabled(shop)) return [];

  const { services } = state(shop);
  if (!Array.isArray(services)) return [];

  return services
    .filter((service) => service && typeof service === 'object')
    .map((service) => ({
      key: typeof service.key === 'string' ? service.key : null,
      label:
        typeof service.label === 'string' && service.label.trim() !== ''
          ? service.label
          : null,
      durationMin: toDuration(service.duration_min),
      priceEur: toPrice(service.price_eur),
    }))
    .filter((service) => service.label !== null);
}

const WEEKDAYS_EN = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const WEEKDAY_LABELS_FR = {
  monday: 'Lundi',
  tuesday: 'Mardi',
  wednesday: 'Mercredi',
  thursday: 'Jeudi',
  friday: 'Vendredi',
  saturday: 'Samedi',
  sunday: 'Dimanche',
};

/** Une fenêtre horaire affichable ("09:00–12:00"), ou null si mal formée. */
function toWindowLabel(window) {
  if (!window || typeof window !== 'object') return null;
  const { start, end } = window;
  if (typeof start !== 'string' || start === '') return null;
  if (typeof end !== 'string' || end === '') return null;
  return `${start}–${end}`;
}

/**
 * Le gabarit hebdo du compte Fixer Lite (FIXERLIGHT-068), au format du
 * tableau d'horaires de la fiche : `null` si la fiche n'est pas Fixer Lite,
 * OU si son gabarit n'a AUCUNE fenêtre exploitable — dans les deux cas le
 * composant retombe entièrement sur les horaires Google. Sinon, les 7 jours
 * dans l'ordre Lundi → Dimanche, « Fermé » pour un jour sans fenêtre.
 *
 * Le repli est nécessaire, pas cosmétique : `opening_hours` est absent
 * lorsque le front est déployé avant l'api (ou un aperçu branché sur une api
 * de staging en retard), et posé à `[]` par
 * `fixer-lite:create-account --shop=<id>` (`FixerLiteCreateAccountCommand`),
 * qui rattache un compte à une fiche annuaire EXISTANTE — Google et ses vrais
 * horaires compris. Rendre « Fermé » sur les 7 jours dans ces deux cas
 * afficherait un atelier fermé toute la semaine alors qu'il ne l'est pas :
 * pire que le « Non renseigné » que ce ticket corrige.
 *
 * `settings.opening_hours` (workshop_accounts) est la MÊME source que le
 * calcul des créneaux réservables côté api (AvailabilityService) : quand AU
 * MOINS un jour porte une fenêtre, un autre jour absent du gabarit n'y
 * produit aucun créneau, donc « Fermé » pour CE jour-là est la lecture
 * directe de cette source, pas une supposition.
 */
export function fixerLiteWeeklyHours(shop) {
  if (!isFixerLiteEnabled(shop)) return null;

  const { opening_hours: openingHours } = state(shop);
  const template =
    openingHours && typeof openingHours === 'object' ? openingHours : {};

  const rows = WEEKDAYS_EN.map((day) => {
    const windows = Array.isArray(template[day]) ? template[day] : [];
    const labels = windows.map(toWindowLabel).filter((label) => label !== null);
    return {
      day: WEEKDAY_LABELS_FR[day],
      hours: labels.length > 0 ? labels.join(', ') : 'Fermé',
      hasWindow: labels.length > 0,
    };
  });

  if (!rows.some((row) => row.hasWindow)) return null;

  return rows.map(({ day, hours }) => ({ day, hours }));
}

/**
 * La fiche Fixer Lite a-t-elle un gabarit exploitable ? C'est le SEUL garde à
 * poser avant de préférer le badge/les horaires Fixer Lite aux équivalents
 * Google — jamais `isFixerLiteEnabled(shop)` seul, qui reste vrai même
 * gabarit vide (voir `fixerLiteWeeklyHours`).
 */
export function fixerLiteHasOpeningHours(shop) {
  return fixerLiteWeeklyHours(shop) !== null;
}

const PARIS_TZ = 'Europe/Paris';

/**
 * Le jour et l'heure courants en Europe/Paris, quel que soit le fuseau du
 * runtime (navigateur du visiteur, ou horloge UTC du serveur SSR) : l'api
 * (`AvailabilityService`, `OpeningHours::isOpenAt`) raisonne en heure de
 * Paris, donc « Ouvert maintenant » doit comparer au même référentiel, sous
 * peine de statut décalé pour un visiteur hors fuseau Paris ou au rendu SSR.
 */
function parisNow() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: PARIS_TZ,
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const byType = {};
  parts.forEach((part) => {
    byType[part.type] = part.value;
  });
  return {
    weekday: byType.weekday.toLowerCase(),
    hour: parseInt(byType.hour, 10),
    minute: parseInt(byType.minute, 10),
  };
}

/** La ligne du jour courant (Paris) dans le gabarit Fixer Lite, ou null si aucun gabarit exploitable. */
function todayRow(shop) {
  const hours = fixerLiteWeeklyHours(shop);
  if (hours === null) return null;
  const todayLabel = WEEKDAY_LABELS_FR[parisNow().weekday];
  return hours.find((row) => row.day === todayLabel) || null;
}

/**
 * Les horaires du jour (Europe/Paris), au format du gabarit Fixer Lite
 * ("09:00–18:00" ou "Fermé"), ou `null` si la fiche n'est pas Fixer Lite, ou
 * si son gabarit n'a aucune fenêtre exploitable — le composant retombe alors
 * sur `getHorairesAujourdHui(details.horaires)`. Vérifier avec
 * `fixerLiteHasOpeningHours(shop)`, jamais `isFixerLiteEnabled(shop)` seul.
 */
export function fixerLiteHoursToday(shop) {
  const row = todayRow(shop);
  return row ? row.hours : null;
}

/**
 * La fiche est-elle ouverte maintenant (Europe/Paris), d'après le gabarit
 * Fixer Lite ?
 *
 * `null` quand la fiche n'est pas Fixer Lite, OU quand son gabarit n'a aucune
 * fenêtre exploitable — le composant retombe alors sur
 * `isOpenNowFromHoraires(details.horaires)` — `true`/`false` sinon, jamais
 * `null` pour un gabarit exploitable : un jour sans fenêtre DANS ce gabarit
 * est un fait connu (fermé), pas une donnée manquante. C'est ce qui garantit
 * que le badge « Ouvert »/« Fermé » de la fiche et son tableau d'horaires ne
 * se contredisent plus jamais (FIXERLIGHT-068) : les deux lisent la même
 * source, et retombent ensemble sur Google si cette source est vide.
 */
export function fixerLiteOpenNow(shop) {
  const today = todayRow(shop);
  if (today === null) return null;
  if (today.hours === 'Fermé') return false;

  const { hour, minute } = parisNow();
  const currentMinutes = hour * 60 + minute;
  const rangeRegex = /(\d{1,2}):(\d{2})\s*–\s*(\d{1,2}):(\d{2})/g;
  let match;
  // eslint-disable-next-line no-cond-assign
  while ((match = rangeRegex.exec(today.hours)) !== null) {
    const start = parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
    const end = parseInt(match[3], 10) * 60 + parseInt(match[4], 10);
    if (currentMinutes >= start && currentMinutes < end) return true;
  }
  return false;
}
